import { AuthToken } from '@libs/models';

const baseUrl = {
  dev: 'https://api.carcareers.com',
  prod: 'https://v1.carcareers.com',
  local: 'http://localhost:3000',
  test: 'http://localhost:3000',
};

const env = process.env.NX_ENVIRONMENT ?? 'dev';

interface RequestConfig {
  url: string;
  method?: string;
  body?: any;
  headers?: Record<string, string>;
  isFileStream?: boolean;
}

export const baseFileQuery = async (config: RequestConfig): Promise<Response> => {
  const { url, method = 'GET', body, headers = {}, isFileStream = false } = config;
  const fullUrl = `${baseUrl[env as keyof typeof baseUrl] ?? baseUrl.dev}${url}`;

  const prepareHeaders = (requestHeaders: Record<string, string>) => {
    const session = JSON.parse(localStorage.getItem('session') || '{}');
    const token = session?.auth?.token;

    if (!isFileStream && !requestHeaders['Content-Type']) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    requestHeaders['Accept'] = 'application/json';

    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    return requestHeaders;
  };

  const makeRequest = async (requestConfig: RequestConfig): Promise<Response> => {
    const preparedHeaders = prepareHeaders({ ...headers });
    
    const fetchConfig: RequestInit = {
      method,
      headers: preparedHeaders,
    };

    if (body && method !== 'GET') {
      fetchConfig.body = isFileStream ? body : JSON.stringify(body);
    }

    return fetch(fullUrl, fetchConfig);
  };

  let response = await makeRequest(config);

  if (response.status === 401) {
    const session = JSON.parse(localStorage.getItem('session') || '{}');
    const refreshToken = session?.auth?.refreshToken;
    const refreshExpires = session?.auth?.refreshExpires;

    if (refreshToken && refreshExpires && new Date(refreshExpires) > new Date()) {
      const refreshResponse = await fetch(`${baseUrl[env as keyof typeof baseUrl] ?? baseUrl.dev}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (refreshResponse.ok) {
        const data = await refreshResponse.json() as AuthToken;
        const updatedSession = {
          ...session,
          auth: {
            token: data.token,
            expires: new Date(data.expiresAt ?? new Date()),
            refreshToken: data.refreshToken || refreshToken,
            refreshExpires: new Date(data.refreshExpiresAt ?? refreshExpires),
          },
        };

        localStorage.setItem('session', JSON.stringify(updatedSession));

        response = await makeRequest(config);
      } else {
        localStorage.removeItem('session');
        window.location.href = '/auth/login';
      }
    } else {
      localStorage.removeItem('session');
      window.location.href = '/auth/login';
    }
  }

  return response;
};